'use client'

import { useEffect, useRef } from 'react'

const HISTORY_KEY = '__aaregVisning'

type Entry<T extends string> = { view: T; stack: T[] }

function currentStack(): string[] {
    return (typeof window !== 'undefined' && window.history.state?.[HISTORY_KEY]?.stack) || []
}

/** Visningen før den gjeldende i appens egen historikk, om det finnes en. */
export function previousView(): string | undefined {
    const stack = currentStack()
    return stack.length > 1 ? stack[stack.length - 2] : undefined
}

/** Antall historikkoppføringer appen har lagt oppå siden brukeren kom fra. */
export function viewHistoryDepth(): number {
    return Math.max(currentStack().length - 1, 0)
}

/**
 * Holder en visningstilstand i sync med nettleserhistorikken, slik at Tilbake/Fremover
 * flytter seg mellom visninger som deler samme URL. Må kalles fra en komponent som
 * står montert i alle visningene.
 *
 * Vi eier tilstanden vi legger i history.state selv, fordi Next.js App Router skriver
 * om history.state under navigasjon.
 */
export function useViewHistory<T extends string>(view: T, rootView: T, setView: (view: T) => void) {
    const setViewRef = useRef(setView)
    setViewRef.current = setView

    const entryRef = useRef<Entry<T>>({ view, stack: [view] })

    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            const entry: Entry<T> = event.state?.[HISTORY_KEY] ?? { view: rootView, stack: [rootView] }
            entryRef.current = entry
            setViewRef.current(entry.view)
        }

        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [rootView])

    useEffect(() => {
        const { view: entryView, stack } = entryRef.current
        if (view === entryView) return

        const target = stack.lastIndexOf(view)
        if (target !== -1) {
            // Tilbake i appen -> gjenbruk historikkoppføringene vi allerede har.
            entryRef.current = { view, stack: stack.slice(0, target + 1) }
            window.history.go(target - (stack.length - 1))
            return
        }

        const entry: Entry<T> = { view, stack: [...stack, view] }
        entryRef.current = entry
        window.history.pushState({ ...window.history.state, [HISTORY_KEY]: entry }, '', window.location.href)
    }, [view])
}
